import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link as RouterLink } from "react-router-dom";

import { useStyle } from "../../context/styleContext/styleContext";
import SaleHeroBanner from "../../components/SaleHeroBanner/SaleHeroBanner.jsx"
import Seo from "../../components/Seo";

import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import SubscriptionsRoundedIcon from "@mui/icons-material/SubscriptionsRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import { Facebook, YouTube, X } from "@mui/icons-material";

import HomePageProductCard from "../../components/ProductCard/HomePageProductCard";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import HomeFeaturedProductCard from "../../components/ProductCard/HomeFeaturedProductCard";

export default function Home() {
  const { style } = useStyle();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [dataForAllProducts, setDataForAllProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const getProductInfo = useCallback(async () => {
    try {
      setLoadingProducts(true);
      const { data } = await axios.post("/api/get-products-available-on-website");

      if (data) {
        setDataForAllProducts(data);
      } else {
        console.log("data error");
      }
    } catch (error) {
      console.log("Error retrieving data...", error);
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  useEffect(() => {
    getProductInfo();
  }, [getProductInfo]);

  const featuredProduct = useMemo(() => {
    return dataForAllProducts[12] || dataForAllProducts[7] || null;
  }, [dataForAllProducts]);

  const newReleaseProducts = useMemo(() => {
    let data = [dataForAllProducts[1], dataForAllProducts[4], dataForAllProducts[6]]

    return data;
  }, [dataForAllProducts]);

  return (
    <>
      <Seo
        title="Parish Audio | Professional Audio Plugins"
        description="Discover Parish Audio plugins for mixing, sound design, songwriting, and modern production. Instant downloads for Mac and Windows."
        canonical="https://parishaudio.com/"
      />

      <Box
        sx={{
          width: "100%",
          maxWidth: "100vw",
          overflowX: "hidden",
          backgroundColor: "black",
          px: { xs: 1.25, sm: 1.5, md: 2.5 },
          py: { xs: 1.5, md: 2.5 },
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 1120,
            mx: "auto",
            minWidth: 0,
          }}
        >
          <SaleHeroBanner />
          <br />
          <Stack spacing={{ xs: 2, md: 2.5, mt: 1 }}>
            <Paper
              sx={{
                position: "relative",
                overflow: "hidden",
                borderRadius: "20px",
                width: "100%",
                minHeight: { xs: 360, sm: 400, md: 450, lg: 450 },
                display: "flex",
                alignItems: { xs: "stretch", md: "center" },
                backgroundColor: "black",
                border: `1px solid ${style.color2}`,
                boxSizing: "border-box",
                backgroundImage: `
                  linear-gradient(
                    90deg,
                    rgba(8, 12, 18, 0.84) 0%,
                    rgba(8, 12, 18, 0.74) 34%,
                    rgba(8, 12, 18, 0.52) 62%,
                    rgba(8, 12, 18, 0.46) 100%
                  ),
                  url('/assets/HeroBanner.webp')
                `,
                backgroundSize: "cover",
                backgroundPosition: "center center",
                backgroundRepeat: "no-repeat",
              }}
              elevation={0}
            >
              <Box
                sx={{
                  width: "100%",
                  p: { xs: 2, sm: 3, md: 4 },
                  display: "flex",
                  alignItems: "center",
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    maxWidth: 620,
                    minWidth: 0,
                  }}
                >
                  <Stack spacing={1.25} alignItems="flex-start">
                    <Chip
                      icon={<GraphicEqRoundedIcon />}
                      label="Professional Audio Plugins"
                      sx={{
                        borderRadius: "5px",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: "rgba(255,255,255,0.08)",
                        border: "1px solid rgba(255,255,255,0.10)",
                      }}
                    />
<Typography
                      component="h1"
                      sx={{
                        color: style.color4,
                        fontWeight: 800,
                        lineHeight: 1.02,
                        fontSize: {
                          xs: "2rem",
                          sm: "2.5rem",
                          md: "3.5rem",
                        },
                        letterSpacing: "-0.04em",
                        textShadow: "0 6px 24px rgba(0,0,0,0.35)",
                        mb:5,
                      }}
                    >
                      Parish Audio
                    </Typography>
                    <Typography
                      component="h2"
                      sx={{
                        color: style.color4,
                        fontWeight: 800,
                        lineHeight: 1.02,
                        fontSize: {
                          xs: "1.25rem",
                          sm: "1.5rem",
                          md: "1.75rem",
                        },
                        letterSpacing: "-0.04em",
                        textShadow: "0 6px 24px rgba(0,0,0,0.35)",
                      }}
                    >
                      Powerful sound.
                      <br />
                      Designed for modern production.
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.88)",
                        maxWidth: 560,
                        fontSize: {
                          xs: "0.98rem",
                          sm: "1.05rem",
                          md: "1.14rem",
                        },
                        lineHeight: 1.7,
                        textShadow: "0 4px 20px rgba(0,0,0,0.28)",
                      }}
                    >
                      Professional tools for mixing, sound design, songwriting,
                      and recording. Available on Mac and Windows.
                    </Typography>

                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.25}
                      sx={{ pt: 0.75, width: { xs: "100%", sm: "auto" } }}
                    >
                      <Button
                        component={RouterLink}
                        to="/products"
                        sx={{
                          borderRadius: "5px",
                          textTransform: "none",
                          fontWeight: 700,
                          color: style.color4,
                          backgroundColor: style.accentGreen,
                          minWidth: { sm: 180 },
                          "&:hover": {
                            backgroundColor: style.accentGreen,
                            filter: "brightness(1.05)",
                          },
                        }}
                      >
                        Browse Plugins
                      </Button>

                      <Button
                        component={RouterLink}
                        to="/subscribe"
                        startIcon={<SubscriptionsRoundedIcon />}
                        sx={{
                          borderRadius: "5px",
                          textTransform: "none",
                          fontWeight: 700,
                          color: style.color4,
                          backgroundColor: "rgba(255,255,255,0.06)",
                          border: "1px solid rgba(255,255,255,0.14)",
                          minWidth: { sm: 180 },
                          "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.12)",
                          },
                        }}
                      >
                        Start Free Trial
                      </Button>
                    </Stack>

                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ pt: 0.5 }}
                    >
                      <HeroPill
                        icon={<DownloadRoundedIcon sx={{ fontSize: 18 }} />}
                        text="Instant downloads"
                        style={style}
                      />
                      <HeroPill
                        icon={<CheckCircleRoundedIcon sx={{ fontSize: 18 }} />}
                        text="Mac + Windows"
                        style={style}
                      />
                    </Stack>
                  </Stack>
                </Box>
              </Box>
            </Paper>
            {featuredProduct && (
              <Paper
                elevation={0}
                sx={{
                  width: "100%",
                  minWidth: 0,
                  borderRadius: "20px",
                  overflow: "hidden",
                  backgroundColor: style.color1,
                  border: `1px solid ${style.color2}`,
                  boxSizing: "border-box",
                }}
              >
                <Box
                  sx={{
                    px: { xs: 1.5, sm: 2, md: 2.5 },
                    py: { xs: 1.25, md: 1.5 },
                    borderBottom: `1px solid rgba(255,255,255,0.08)`,
                    backgroundColor: style.darkPanel,
                  }}
                >
                  <Typography
                    component="h3"
                    sx={{
                      color: style.color4,
                      fontWeight: 800,
                      fontSize: { xs: "1.25rem", md: "1.5rem" },
                    }}
                  >
                    Featured
                  </Typography>
                </Box>

                <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
                  <HomeFeaturedProductCard productData={featuredProduct} />
                </Box>
              </Paper>
            )}

            <Paper
              elevation={0}
              sx={{
                width: "100%",
                minWidth: 0,
                borderRadius: "20px",
                overflow: "hidden",
                backgroundColor: style.color1,
                border: `1px solid ${style.color2}`,
                boxSizing: "border-box",
              }}
            >
              <Box
                sx={{
                  px: { xs: 1.5, sm: 2, md: 2.5 },
                  py: { xs: 1.5, md: 2 },
                  backgroundColor: style.darkPanel,
                  borderBottom: `1px solid rgba(255,255,255,0.08)`,
                }}
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={2}
                  alignItems={{ xs: "flex-start", md: "center" }}
                  justifyContent="space-between"
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Chip
                      label="Subscription"
                      sx={{
                        mb: 1,
                        borderRadius: "5px",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: "rgba(255,255,255,0.06)",
                      }}
                    />
                    <Typography
                      sx={{
                        color: style.color4,
                        fontWeight: 800,
                        fontSize: { xs: "1.35rem", md: "1.8rem" },
                        lineHeight: 1.08,
                      }}
                    >
                      Full Access Pass - Get everything with one subscription
                    </Typography>
                    <Typography
                      sx={{
                        color: style.color3,
                        lineHeight: 1.7,
                        mt: 0.9,
                        maxWidth: 760,
                      }}
                    >
                      Try the full lineup of plugins with a 7-day free trial, then $9.95 per month. Cancel anytime.
                    </Typography>
                  </Box>

                  <Stack
                    direction={{ xs: "column", sm: "row", md: "column" }}
                    spacing={1}
                    sx={{ width: { xs: "100%", md: "auto" } }}
                  >
                    <Button
                      component={RouterLink}
                      to="/subscribe"
                      startIcon={<SubscriptionsRoundedIcon />}
                      sx={{
                        borderRadius: "5px",
                        textTransform: "none",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: style.accentGreen,
                        minWidth: { md: 220 },
                        "&:hover": {
                          backgroundColor: style.accentGreen,
                          filter: "brightness(1.05)",
                        },
                      }}
                    >
                      Start Your Free Trial
                    </Button>

                    <Button
                      component={RouterLink}
                      to="/products"
                      endIcon={<ArrowForwardRoundedIcon />}
                      sx={{
                        borderRadius: "5px",
                        textTransform: "none",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: "rgba(255,255,255,0.05)",
                        border: "1px solid rgba(255,255,255,0.10)",
                        minWidth: { md: 220 },
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.10)",
                        },
                      }}
                    >
                      Explore Plugins
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                width: "100%",
                minWidth: 0,
                borderRadius: "20px",
                overflow: "hidden",
                backgroundColor: style.color1,
                border: `1px solid ${style.color2}`,
                boxSizing: "border-box",
              }}
            >

<Box

  sx={{
    px: { xs: 1.5, sm: 2, md: 2.5 },
    py: { xs: 1.25, md: 1.5 },
    borderBottom: `1px solid rgba(255,255,255,0.08)`,
    backgroundColor: style.darkPanel,
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    alignItems: { xs: "flex-start", sm: "center" },
    justifyContent: "space-between",
    gap: 1.25,
  }}
>
  <Typography
    component="h3"
    sx={{
      color: style.color4,
      fontWeight: 800,
      fontSize: { xs: "1.25rem", md: "1.5rem" },
    }}
  >
    Suggested Plugins:
  </Typography>

  <Button
    component={RouterLink}
    to="/products"
    endIcon={<ArrowForwardRoundedIcon />}
    sx={{
      borderRadius: "5px",
      textTransform: "none",
      fontWeight: 700,
      color: style.color4,
      backgroundColor: "rgba(255,255,255,0.05)",
      border: "1px solid rgba(255,255,255,0.10)",
      minWidth: { xs: "100%", sm: 180, md: 220 },
      "&:hover": {
        backgroundColor: "rgba(255,255,255,0.10)",
      },
    }}
  >
    Shop All Plugins
  </Button>
</Box>


              {/* <Box
                sx={{
                  px: { xs: 1.5, sm: 2, md: 2.5 },
                  py: { xs: 1.25, md: 1.5 },
                  borderBottom: `1px solid rgba(255,255,255,0.08)`,
                  backgroundColor: style.darkPanel,
                }}
              >
                <div style={{display:'flex', flexDirection:'row'}}>
                <Typography
                  component="h3"
                  sx={{
                    color: style.color4,
                    fontWeight: 800,
                    fontSize: { xs: "1.25rem", md: "1.5rem" },
                  }}
                >
                  Products:
                </Typography>

                <Button
                      component={RouterLink}
                      to="/products"
                      endIcon={<ArrowForwardRoundedIcon />}
                      sx={{
                        borderRadius: "5px",
                        textTransform: "none",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: "rgba(255,255,255,0.05)",
                        border: "1px solid rgba(255,255,255,0.10)",
                        minWidth: { md: 220 },
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.10)",
                        },
                      }}
                    >
                      Shop All Plugins
                    </Button>

                </div>
                
              </Box> */}

              <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
                {loadingProducts ? (
                  <Box sx={{ py: 4 }}>
                    <LoadingSpinner />
                  </Box>
                ) : (
                  <>
                    {!isMobile && newReleaseProducts.length > 0 && (
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                          gap: 2,
                        }}
                      >
                        {newReleaseProducts.map((product, index) => (
                          <Box
                            key={product?._id || product?.productURI || index}
                            sx={{ minWidth: 0 }}
                          >
                            <HomePageProductCard productData={product} />
                          </Box>
                        ))}
                      </Box>
                    )}

                    {isMobile && (
                      <Stack spacing={1.25}>
                        {newReleaseProducts && newReleaseProducts.length > 0 ? (
                          newReleaseProducts.map((product, index) => (
                            <Box
                              key={product?._id || product?.productURI || index}
                              sx={{ minWidth: 0 }}
                            >
                              <HomePageProductCard productData={product} />
                            </Box>
                          ))
                        ) : null}
                      </Stack>
                    )}
                  </>
                )}
              </Box>
            </Paper>
            <Paper
              elevation={0}
              sx={{
                width: "100%",
                minWidth: 0,
                borderRadius: "20px",
                backgroundColor: style.darkPanel,
                border: `1px solid ${style.color2}`,
                p: { xs: 2, sm: 2.5, md: 3 },
                boxSizing: "border-box",
                overflow: "hidden",
              }}
            >
              <Stack spacing={1.5} alignItems="center" textAlign="center">
                <Typography
                  sx={{
                    color: style.color4,
                    fontWeight: 800,
                    fontSize: { xs: "1.55rem", md: "2rem" },
                    lineHeight: 1.1,
                  }}
                >
                  Ready to find the perfect tool for your sound?
                </Typography>

                <Typography
                  sx={{
                    color: style.color3,
                    lineHeight: 1.7,
                    maxWidth: 680,
                  }}
                >
                  Browse the full plugin catalog, or try everything free with the Full Access Pass.
                </Typography>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  <Button
                    component={RouterLink}
                    to="/products"
                    sx={{
                      borderRadius: "5px",
                      textTransform: "none",
                      fontWeight: 800,
                      color: style.color4,
                      backgroundColor: style.accentGreen,
                      minWidth: { sm: 180 },
                      minHeight: 44,
                      "&:hover": {
                        backgroundColor: style.accentGreen,
                        filter: "brightness(1.05)",
                      },
                    }}
                  >
                    Browse All Plugins
                  </Button>

                  <Button
                    component={RouterLink}
                    to="/subscribe"
                    sx={{
                      borderRadius: "5px",
                      textTransform: "none",
                      fontWeight: 800,
                      color: style.color4,
                      backgroundColor: "rgba(255,255,255,0.05)",
                      border: "1px solid rgba(255,255,255,0.10)",
                      minWidth: { sm: 180 },
                      minHeight: 44,
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.10)",
                      },
                    }}
                  >
                    Start Free Trial
                  </Button>
                </Stack>
              </Stack>
            </Paper>


            <Paper
  elevation={0}
  sx={{
    width: "100%",
    minWidth: 0,
    borderRadius: "20px",
    backgroundColor: style.color1,
    border: `1px solid ${style.color2}`,
    p: { xs: 1.5, sm: 2 },
    boxSizing: "border-box",
    overflow: "hidden",
  }}
>
  <Stack spacing={1.25} alignItems="center" textAlign="center" sx={{ minWidth: 0 }}>
    <Typography
      sx={{
        color: style.color4,
        fontWeight: 800,
        fontSize: { xs: "1.1rem", md: "1.25rem" },
      }}
    >
      Find us on
    </Typography>

    <Box
      sx={{
        width: "100%",
        minWidth: 0,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: { xs: 2, sm: 2.25 },
        flexWrap: "wrap",
        boxSizing: "border-box",
      }}
    >
      <a
        href="https://facebook.com/parishaudio"
        aria-label="Visit Parish Audio on Facebook"
        style={{ display: "flex", alignItems: "center", flexShrink: 0 }}
      >
        <Facebook
          sx={{
            fontSize: 34,
            color: "#1877F2",
            transition: "transform 0.2s ease, filter 0.2s ease",
            "&:hover": {
              transform: "scale(1.08)",
              filter: "brightness(1.08)",
            },
          }}
        />
      </a>

      <a
        href="https://x.com/Parish_Audio"
        aria-label="Visit Parish Audio on X"
        style={{ display: "flex", alignItems: "center", flexShrink: 0 }}
      >
        <X
          sx={{
            fontSize: 29,
            color: style.color4,
            transition: "transform 0.2s ease, filter 0.2s ease",
            "&:hover": {
              transform: "scale(1.08)",
              filter: "brightness(1.08)",
            },
          }}
        />
      </a>

      <a
        href="https://youtube.com/@ParishAudio"
        aria-label="Visit Parish Audio on YouTube"
        style={{ display: "flex", alignItems: "center", flexShrink: 0 }}
      >
        <YouTube
          sx={{
            fontSize: 40,
            color: "#FF0000",
            transition: "transform 0.2s ease, filter 0.2s ease",
            "&:hover": {
              transform: "scale(1.08)",
              filter: "brightness(1.08)",
            },
          }}
        />
      </a>
    </Box>
  </Stack>
</Paper>
          </Stack>
        </Box>
      </Box>
    </>
  );
}

function HeroPill({ icon, text, style }) {
  return (
    <Stack
      direction="row"
      spacing={0.75}
      alignItems="center"
      sx={{
        px: 1.05,
        py: 0.7,
        borderRadius: "999px",
        backgroundColor: "rgba(255,255,255,0.06)",
        border: "1px solid rgba(255,255,255,0.10)",
        color: style.color4,
      }}
    >
      <Box sx={{ display: "flex", color: style.color3 }}>{icon}</Box>
      <Typography sx={{ fontSize: "0.9rem", fontWeight: 700 }}>{text}</Typography>
    </Stack>
  );
}
// import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
// import axios from "axios";
// import { Link as RouterLink } from "react-router-dom";

// import { useStyle } from "../../context/styleContext/styleContext";
// import Seo from "../../components/Seo";

// import {
//   Box,
//   Button,
//   Chip,
//   Paper,
//   Stack,
//   Typography,
//   useMediaQuery,
//   useTheme,
// } from "@mui/material";

// import { CheckCircleRounded } from "@mui/icons-material";
// import { Facebook, YouTube, X } from "@mui/icons-material";

// //import SubscribeBanner from "../../components/SubscribeBanner/SubscribeBanner";
// import HomePageProductCard from "../../components/ProductCard/HomePageProductCard";
// import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
// import HomeFeaturedProductCard from "../../components/ProductCard/HomeFeaturedProductCard";

// export default function Home() {
//   const { style } = useStyle();
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down("md"));

//   const [dataForAllProducts, setDataForAllProducts] = useState([]);
//   const [loadingProducts, setLoadingProducts] = useState(true);

//   const topContainer = useRef(null);

//   useEffect(() => {
//     window.scrollTo({ top: 0, behavior: "auto" });
//   }, []);

//   const getProductInfo = useCallback(async () => {
//     try {
//       setLoadingProducts(true);
//       const { data } = await axios.post("/api/get-products-available-on-website");

//       if (data) {
//         setDataForAllProducts(data);
//       } else {
//         console.log("data error");
//       }
//     } catch (error) {
//       console.log("Error retrieving data...", error);
//     } finally {
//       setLoadingProducts(false);
//     }
//   }, []);

//   useEffect(() => {
//     getProductInfo();
//   }, [getProductInfo]);

//   const featuredProduct = useMemo(() => {
//     return dataForAllProducts[3] || null;
//   }, [dataForAllProducts]);

//   const newReleaseProducts = useMemo(() => {
//     return dataForAllProducts.slice(0, 3);
//   }, [dataForAllProducts]);

//   return (
//     <>
//       <Seo
//         title="Parish Audio | Professional Audio Plugins"
//         description="Discover Parish Audio plugins for mixing, sound design, songwriting, and modern production. Instant downloads for Mac and Windows."
//         canonical="https://parishaudio.com/"
//       />

//       <Box>
//         <Box className="dark-alpha-80">
//           <Paper
//             ref={topContainer}
//             sx={{
//               position: "relative",
//               overflow: "hidden",
//               borderRadius: 3,
//               mt: 0,
//               width: "95%",
//               maxWidth: "1000px",
//               mx: "auto",
//               minHeight: { xs: 460, sm: 500, md: 470, lg: 520 },
//               display: "flex",
//               alignItems: { xs: "stretch", md: "center" },
//               backgroundColor: "black",
//               backgroundImage: `
//                 linear-gradient(
//                   90deg,
//                   rgba(8, 12, 18, 0.92) 0%,
//                   rgba(8, 12, 18, 0.82) 33%,
//                   rgba(8, 12, 18, 0.58) 57%,
//                   rgba(8, 12, 18, 0.30) 100%
//                 ),
//                 url('/assets/HeroBanner.webp')
//               `,
//               backgroundSize: "cover",
//               backgroundPosition: "center",
//               backgroundRepeat: "no-repeat",
//             }}
//             elevation={6}
//           >
//             <Box
//               sx={{
//                 width: "100%",
//                 p: { xs: 2, sm: 2.5, md: 3 },
//                 display: "flex",
//                 alignItems: { xs: "flex-end", md: "center" },
//               }}
//             >
//               <Box sx={{ width: "100%", maxWidth: { xs: "100%", md: 580 } }}>
//                 <Stack spacing={{ xs: 1.6, md: 2.25 }} alignItems="flex-start">
//                   <Chip
//                     label="Professional Audio Plugins"
//                     sx={{
//                       bgcolor: "rgba(255,255,255,0.10)",
//                       color: "white",
//                       fontWeight: 700,
//                       backdropFilter: "blur(6px)",
//                     }}
//                   />

//                   <Typography
//                     variant="h1"
//                     sx={{
//                       color: "white",
//                       fontWeight: 800,
//                       lineHeight: 1.02,
//                       fontSize: {
//                         xs: "2.35rem",
//                         sm: "3rem",
//                         md: "4rem",
//                       },
//                       textShadow: "0 4px 24px rgba(0,0,0,0.45)",
//                     }}
//                   >
//                     Parish Audio
//                   </Typography>

//                   <Typography
//                     sx={{
//                       color: "rgba(255,255,255,0.92)",
//                       fontWeight: 700,
//                       lineHeight: 1.15,
//                       fontSize: {
//                         xs: "1.2rem",
//                         sm: "1.45rem",
//                         md: "2rem",
//                       },
//                       textShadow: "0 4px 24px rgba(0,0,0,0.4)",
//                     }}
//                   >
//                     Powerful sound. Built for modern production.
//                   </Typography>

//                   <Typography
//                     sx={{
//                       color: "rgba(255,255,255,0.82)",
//                       fontWeight: 400,
//                       maxWidth: 540,
//                       fontSize: {
//                         xs: "0.98rem",
//                         sm: "1.05rem",
//                         md: "1.2rem",
//                       },
//                       textShadow: "0 3px 18px rgba(0,0,0,0.4)",
//                     }}
//                   >
//                     Explore creative tools for mixing, sound design, songwriting,
//                     and everyday creative sessions. Available on Mac and Windows.
//                   </Typography>

//                   <Stack
//                     direction={{ xs: "column", sm: "row" }}
//                     spacing={{ xs: 0.75, sm: 2.5 }}
//                     sx={{
//                       pt: 0.5,
//                       flexWrap: "wrap",
//                       width: "100%",
//                     }}
//                   >
//                     <Stack direction="row" spacing={1} alignItems="center">
//                       <CheckCircleRounded sx={{ color: "white" }} fontSize="small" />
//                       <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)" }}>
//                         Instant download
//                       </Typography>
//                     </Stack>

//                     <Stack direction="row" spacing={1} alignItems="center">
//                       <CheckCircleRounded sx={{ color: "white" }} fontSize="small" />
//                       <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)" }}>
//                         VST3 plugins
//                       </Typography>
//                     </Stack>

//                     <Stack direction="row" spacing={1} alignItems="center">
//                       <CheckCircleRounded sx={{ color: "white" }} fontSize="small" />
//                       <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)" }}>
//                         Mac + Windows
//                       </Typography>
//                     </Stack>
//                   </Stack>

//                   <Stack
//                     direction={{ xs: "column", sm: "row" }}
//                     spacing={1.5}
//                     sx={{
//                       pt: { xs: 1.25, sm: 0.5 },
//                       width: { xs: "100%", sm: "auto" },
//                       mt: { xs: "auto", md: 0 },
//                     }}
//                   >
//                     <Button
//                       variant="contained"
//                       size="large"
//                       component={RouterLink}
//                       to="/plugins"
//                       fullWidth={isMobile}
//                       sx={{
//                         bgcolor: "white",
//                         color: "primary.main",
//                         fontWeight: 700,
//                         minWidth: { sm: 170 },
//                         "&:hover": {
//                           bgcolor: "rgba(255,255,255,0.92)",
//                         },
//                       }}
//                     >
//                       Browse Plugins
//                     </Button>

//                     <Button
//                       variant="outlined"
//                       size="large"
//                       component={RouterLink}
//                       to="/products/d1-delay"
//                       fullWidth={isMobile}
//                       sx={{
//                         borderColor: "rgba(255,255,255,0.40)",
//                         color: "white",
//                         minWidth: { sm: 170 },
//                         "&:hover": {
//                           borderColor: "rgba(255,255,255,0.65)",
//                           bgcolor: "rgba(255,255,255,0.06)",
//                         },
//                       }}
//                     >
//                       View Featured
//                     </Button>
//                   </Stack>
//                 </Stack>
//               </Box>
//             </Box>
//           </Paper>

//           <Box
//             className="centered-main-container"
//             sx={{
//               display: "flex",
//               flexDirection: "column",
//               width: "95%",
//               maxWidth: "900px",
//               mx: "auto",
//               minHeight: "calc(100vh - 130px)",
//             }}
//           >
//             <Box
//               sx={{
//                 mt: 1,
//                 backgroundColor: style.color1,
//                 borderRadius: "8px",
//                 p: 0.25,
//                 contentVisibility: "auto",
//                 containIntrinsicSize: "600px",
//               }}
//             >
//               <Typography
//                 component="h2"
//                 sx={{ textAlign: "left", mb: 0, ml: "30px", fontWeight: 700 }}
//               >
//                 Featured:
//               </Typography>

//               {featuredProduct && (
//                 <Box
//                   sx={{
//                     minWidth: "100px",
//                     backgroundColor: style.color1,
//                     m: 1,
//                     mt: 1,
//                     filter: "brightness(1.05)",
//                     boxShadow: 4,
//                   }}
//                 >
//                   <HomeFeaturedProductCard productData={featuredProduct} />
//                 </Box>
//               )}
//             </Box>

//             <Box
//               sx={{
//                 my: "25px",
//                 contentVisibility: "auto",
//                 containIntrinsicSize: "300px",
//               }}
//             >
//               {/* <SubscribeBanner /> */}
//             </Box>

//             <Box
//               sx={{
//                 mt: 2,
//                 backgroundColor: style.color1,
//                 borderRadius: "8px",
//                 p: 1,
//                 mb: 3,
//                 contentVisibility: "auto",
//                 containIntrinsicSize: "1200px",
//               }}
//             >
//               <Typography
//                 component="h2"
//                 sx={{ textAlign: "left", mb: "20px", fontWeight: 700 }}
//               >
//                 New Releases
//               </Typography>

//               {!isMobile && newReleaseProducts.length > 0 && (
//                 <Box sx={{ display: "flex", flexDirection: "row", p: 1 }}>
//                   {newReleaseProducts.map((product, index) => (
//                     <Box
//                       key={product?._id || product?.productURI || index}
//                       sx={{
//                         flex: 1,
//                         m: "10px",
//                         boxShadow: 10,
//                         filter: "brightness(1.05)",
//                         minWidth: 0,
//                       }}
//                     >
//                       <HomePageProductCard productData={product} />
//                     </Box>
//                   ))}
//                 </Box>
//               )}

//               {isMobile && (
//                 <Box sx={{ display: "flex", flexDirection: "column" }}>
//                   {dataForAllProducts && dataForAllProducts.length > 0 ? (
//                     dataForAllProducts.map((product, index) => (
//                       <Box
//                         key={product?._id || product?.productURI || index}
//                         sx={{
//                           backgroundColor: style.color1,
//                           m: 0.5,
//                           filter: "brightness(1.05)",
//                           boxShadow: 10,
//                           p: 1,
//                         }}
//                       >
//                         <HomePageProductCard productData={product} />
//                       </Box>
//                     ))
//                   ) : loadingProducts ? (
//                     <Box>
//                       <LoadingSpinner />
//                     </Box>
//                   ) : null}
//                 </Box>
//               )}
//             </Box>
//           </Box>
//         </Box>

//         <Box
//   sx={{
//     width: "95%",
//     maxWidth: "800px",
//     mx: "auto",
//     textAlign: "center",
//     mb: "25px",
//   }}
// >
//   <Typography component="h4" sx={{ mb: "10px", fontWeight: 700 }}>
//     Find us on:
//   </Typography>

//   <Stack
//     direction="row"
//     spacing={2.5}
//     justifyContent="center"
//     alignItems="center"
//     sx={{ mt: 1 }}
//   >
//     <a
//       href="https://facebook.com/parishaudio"
//       aria-label="Visit Parish Audio on Facebook"
//       style={{ display: "flex", alignItems: "center" }}
//     >
//       <Facebook
//         sx={{
//           fontSize: 34,
//           color: "#1877F2",
//           transition: "transform 0.2s ease, filter 0.2s ease",
//           "&:hover": {
//             transform: "scale(1.08)",
//             filter: "brightness(1.08)",
//           },
//         }}
//       />
//     </a>

//     <a
//       href="https://x.com/Parish_Audio"
//       aria-label="Visit Parish Audio on X"
//       style={{ display: "flex", alignItems: "center" }}
//     >
//       <X
//         sx={{
//           fontSize: 30,
//           color: style.color4,
//           transition: "transform 0.2s ease, filter 0.2s ease",
//           "&:hover": {
//             transform: "scale(1.08)",
//             filter: "brightness(1.08)",
//           },
//         }}
//       />
//     </a>

//     <a
//       href="https://youtube.com/@ParishAudio"
//       aria-label="Visit Parish Audio on YouTube"
//       style={{ display: "flex", alignItems: "center" }}
//     >
//       <YouTube
//         sx={{
//           fontSize: 40,
//           color: "#FF0000",
//           transition: "transform 0.2s ease, filter 0.2s ease",
//           "&:hover": {
//             transform: "scale(1.08)",
//             filter: "brightness(1.08)",
//           },
//         }}
//       />
//     </a>
//   </Stack>
// </Box>
//       </Box>
//     </>
//   );
// }

// import {React, useState, useContext, useEffect, useRef} from "react"
// import {useStyle} from '../../context/styleContext/styleContext'

// import axios from 'axios'
// import Seo from '../../components/Seo';
// //import {toast} from 'react-hot-toast'

// import {
//   Box,
//   Button,
//   Card,
//   CardContent,
//   Chip,
//   Container,
//   Divider,
//   Grid,
//   Paper,
//   Rating,
//   Stack,
//   Typography
// } from '@mui/material';
// import {
//   AudioFileRounded,
//   CheckCircleRounded,
//   DownloadRounded,
//   FlashOnRounded
// } from '@mui/icons-material';
// import { InfinityReverb_0_0_1 } from "../../ProductInfo";
// import { Link as RouterLink } from 'react-router-dom';
// import ProductCard from "../../components/ProductCard/ProductCard";
// import SubscribeBanner from "../../components/SubscribeBanner/SubscribeBanner";
// import HomePageProductCard from "../../components/ProductCard/HomePageProductCard";
// import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
// import MobileProductCard from "../Plugins/MobileProductCard";
// import HomeFeaturedProductCard from "../../components/ProductCard/HomeFeaturedProductCard";


// export default function Home(){

//   const { style } = useStyle();
//   const [height, setHeight] = useState(window.innerHeight)
//   const [width, setWidth] = useState(window.setWidth)
//   const [dataForAllProducts, setDataForAllProducts] = useState([])

//   useEffect(() => {
//     function handleResize() {      
//       setWidth(window.innerWidth)
//       setHeight(window.innerHeight)
//   }
  
//     window.addEventListener('resize', handleResize)
//   })

  
// const isMobile = width <= 768;


// const topContainer = useRef();
//     useEffect(() => {
//         // To make sure page starts from the top
//         topContainer.current.scrollIntoView({ block: "end" });
//     }, []);



//     const getProductInfo = async (e) => {
//       try{
//         const {data} = await axios.post('/api/get-all-products')
//         if(data)
//         {
//            // console.log(data)
//            var result = [];
//            var count = 0;

//            for(var i in data){
//                count++;
//            }
//            setDataForAllProducts(data)
//         }
//         else{
//             console.log('data error')
//         }
//       }
//       catch
//       {
//         console.log('Error retrieving data...')
//       }
      
//   }
//   useEffect(() => {
//       getProductInfo();
//   }, []);



//     return(
//         <>
//         <Seo
//           title="Parish Audio | Professional Audio Plugins"
//           description="Discover Parish Audio plugins for mixing, sound design, songwriting, and modern production. Instant downloads for Mac and Windows."
//           canonical="https://parishaudio.com/"
//       />
//         <div>
//           <div className='dark-alpha-80'>
//           <Paper
//             ref={topContainer}
//             sx={{
//               position: 'relative',
//               overflow: 'hidden',
//               borderRadius: 3,
//               mt:0,
//               width:'95%',
//               maxWidth:'1000px',
//               margin:'auto',
//               minHeight: { xs: 380, sm: 430, md: 470, lg: 520 },
//               display: 'flex',
//               alignItems: { xs: 'flex-end', md: 'center' },
//               backgroundColor:'black',
//               backgroundImage: {
//                 xs: `
//                   linear-gradient(
//                     90deg,
//                     rgba(8, 12, 18, 0.92) 0%,
//                     rgba(8, 12, 18, 0.82) 33%,
//                     rgba(8, 12, 18, 0.48) 57%,
//                     rgba(8, 12, 18, 0.20) 100%
//                   ),
//                   url('/assets/HeroBanner.png')
//                 `,
//                 md: `
//                   linear-gradient(
//                     90deg,
//                     rgba(8, 12, 18, 0.92) 0%,
//                     rgba(8, 12, 18, 0.82) 33%,
//                     rgba(8, 12, 18, 0.48) 57%,
//                     rgba(8, 12, 18, 0.20) 100%
//                   ),
//                   url('/assets/HeroBanner.png')
//                 `
//               },
//               backgroundSize: 'cover',
//               backgroundPosition: 'center',
//               backgroundRepeat: 'no-repeat',
//             }}
//               >
//             <Box
//               sx={{
//                 width: '100%',
//                 p: { xs: 2, sm: 2.5, md: 3 },
//                 mt:'0px',
//                 mb:'0px',
//                 display: 'flex',
//                 alignItems: 'center',
//               }}
            
//             >
//               <Box sx={{ maxWidth: { xs: '100%', md: 580 } }}>
//                 <Stack spacing={{ xs: 1.75, md: 2.25 }} alignItems="flex-start">
//                   <Chip
//                     label="Professional Audio Plugins"
//                     sx={{
//                       bgcolor: 'rgba(255,255,255,0.10)',
//                       color: 'white',
//                       fontWeight: 700,
//                       backdropFilter: 'blur(6px)'
//                     }}
//                   />

//                   <Typography
//                     variant="h1"
//                     sx={{
//                       color: 'white',
//                       fontWeight: 800,
//                       lineHeight: 1.02,
//                       fontSize: {
//                         xs: '2.35rem',
//                         sm: '3rem',
//                         md: '4rem'
//                       },
//                       textShadow: '0 4px 24px rgba(0,0,0,0.45)'
//                     }}
//                   >
//                     Parish Audio
//                   </Typography>

//                   <Typography
//                     sx={{
//                       color: 'rgba(255,255,255,0.92)',
//                       fontWeight: 700,
//                       lineHeight: 1.15,
//                       fontSize: {
//                         xs: '1.2rem',
//                         sm: '1.45rem',
//                         md: '2rem'
//                       },
//                       textShadow: '0 4px 24px rgba(0,0,0,0.4)'
//                     }}
//                   >
//                     Powerful sound. Built for modern production.
//                   </Typography>

//                   <Typography
//                     sx={{
//                       color: 'rgba(255,255,255,0.82)',
//                       fontWeight: 400,
//                       maxWidth: 540,
//                       fontSize: {
//                         xs: '0.98rem',
//                         sm: '1.05rem',
//                         md: '1.2rem'
//                       },
//                       textShadow: '0 3px 18px rgba(0,0,0,0.4)'
//                     }}
//                   >
//                     Explore creative tools for mixing, sound design, songwriting,
//                     and everyday creative sessions. Available on Mac and Windows.
//                   </Typography>

//                   <Stack
//                     direction={{ xs: 'column', sm: 'row' }}
//                     spacing={1.5}
//                     sx={{ pt: 0.5, width: { xs: '100%', sm: 'auto' } }}
//                   >
//                     <Button
//                       variant="contained"
//                       size="large"
//                       component={RouterLink}
//                       to="/plugins"
//                       sx={{
//                         bgcolor: 'white',
//                         color: 'primary.main',
//                         fontWeight: 700,
//                         minWidth: { sm: 170 },
//                         '&:hover': {
//                           bgcolor: 'rgba(255,255,255,0.92)'
//                         }
//                       }}
//                     >
//                       Browse Plugins
//                     </Button>

//                     <Button
//                       variant="outlined"
//                       size="large"
//                       component={RouterLink}
//                       //to={featuredProduct ? `/products/${featuredProduct.productURI}` : '/products'}
//                       to={`/products/d1-delay`}
//                       sx={{
//                         borderColor: 'rgba(255,255,255,0.40)',
//                         color: 'white',
//                         minWidth: { sm: 170 },
//                         '&:hover': {
//                           borderColor: 'rgba(255,255,255,0.65)',
//                           bgcolor: 'rgba(255,255,255,0.06)'
//                         }
//                       }}
//                     >
//                       View Featured
//                     </Button>
//                   </Stack>

//                   <Stack
//                     direction={{ xs: 'column', sm: 'row' }}
//                     spacing={{ xs: 0.75, sm: 2.5 }}
//                     sx={{ pt: 0.5, flexWrap: 'wrap' }}
//                   >
//                     <Stack direction="row" spacing={1} alignItems="center">
//                       <CheckCircleRounded sx={{ color: 'white' }} fontSize="small" />
//                       <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
//                         Instant download
//                       </Typography>
//                     </Stack>

//                     <Stack direction="row" spacing={1} alignItems="center">
//                       <CheckCircleRounded sx={{ color: 'white' }} fontSize="small" />
//                       <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
//                         VST3 plugins
//                       </Typography>
//                     </Stack>

//                     <Stack direction="row" spacing={1} alignItems="center">
//                       <CheckCircleRounded sx={{ color: 'white' }} fontSize="small" />
//                       <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
//                         Mac + Windows
//                       </Typography>
//                     </Stack>
//                   </Stack>
//                 </Stack>
//               </Box>
//             </Box>
//           </Paper>
//            <div className='centered-main-container' style={{display:'flex', flexDirection:'column', width:'95%', maxWidth:'900px', backgroundPosition: "center", minHeight:height - 130}}>


//            <Box sx={{mt:1, backgroundColor:style.color1, borderRadius:'8px', p:0.25}}>
//             <h2 style={{textAlign:'left', marginBottom:'0px', marginLeft:'30px'}}>
//              Featured:
//             </h2>
//           {dataForAllProducts[2] && (
//               <Box sx={{minWidth:'100px', backgroundColor:style.color1, m:1, mt:1, filter:'brightness(1.05)', boxShadow:4}}>

//                    <HomeFeaturedProductCard productData={dataForAllProducts[3]} />
//               </Box>
//         )
//       }
//           </Box>

//     <div style={{marginTop:'25px', marginBottom:'25px'}}>
//       <SubscribeBanner />
//     </div>

//           <Box sx={{mt:2, backgroundColor:style.color1, borderRadius:'8px', p:1, mb:3}}>
//             <h2 style={{textAlign:'left', marginBottom:'20px'}}>
//               New Releases
//             </h2>

//           {!isMobile && dataForAllProducts.length > 0 && (
// <>
//            <Box sx={{ display:'flex', flexDirection:'row', p:1}}>
//             <Box sx={{flex:1, margin:'10px', boxShadow:10, filter:'brightness(1.05)'}}>
//               <HomePageProductCard productData={dataForAllProducts[0]} />
//             </Box>
//             <Box sx={{flex:1, margin:'10px', boxShadow:10, filter:'brightness(1.05)'}}>
//               <HomePageProductCard productData={dataForAllProducts[1]} />
//             </Box>
//             <Box sx={{flex:1, margin:'10px', boxShadow:10, filter:'brightness(1.05)'}}>
//               <HomePageProductCard productData={dataForAllProducts[2]} />
//             </Box>


                
//        </Box>
// </>


//           )}


//       {isMobile && (
            
//             <Box sx={{ display:'flex', flexDirection:'column'}}>
//             {dataForAllProducts && dataForAllProducts.length > 0 ? (
  
//               dataForAllProducts.map((product, index) => (
//                 <Box sx={{backgroundColor:style.color1, m:0.5, filter:'brightness(1.05)', boxShadow:10,p:1}}>
//                      <HomePageProductCard key={index} productData={product} />
//                 </Box>
//           ))
//         ) : (
//           <Box>
//                   <LoadingSpinner />
//           </Box>
//         )}
//         </Box>
 
 
 
//            )}

//           </Box>
//           </div>
//           </div>
//           <div style={{width:'95%', maxWidth:'800px', margin:'auto', margin:'auto', textAlign:'center', marginBottom:'25px'}}>
//             <h4 style={{marginBottom:'10px'}}>Find us on:</h4> 
//             <a href="https://facebook.com/parishaudio" style={{paddingTop:'10px'}}><img src='FacebookIcon.png' alt="Facebook icon" style={{maxWidth:'33px', marginLeft:'12px'}} /></a>
//             <a href="https://x.com/Parish_Audio"><img src='XLogoWhite.png'  alt="X icon"style={{maxWidth:'29px', marginLeft:'15px'}} /></a>
//             <a href="https://youtube.com/@ParishAudio"><img src='YoutubeIcon.png' alt="Youtube icon" style={{maxWidth:'40px', marginLeft:'17px'}} /></a>
//             </div>
//         </div>
        
//         </>
//     )
// }

